
import React from 'react';
import { motion } from 'framer-motion';
import ParticleNetwork from './ParticleNetwork';

const CosmicBackground: React.FC = () => {
  return (
    <div className="fixed inset-0 z-0 bg-[#0A0612] overflow-hidden pointer-events-none">
      {/* Base Deep Space Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#08040d] via-[#0f071a] to-[#08040d]" />
      
      {/* Starfield Layer 1: Distant tiny stars */}
      <div className="absolute inset-0 opacity-40">
        <div className="stars-small absolute inset-0" style={{ 
          backgroundImage: 'radial-gradient(1px 1px at 20px 30px, #fff, rgba(0,0,0,0)), radial-gradient(1px 1px at 40px 70px, #fff, rgba(0,0,0,0)), radial-gradient(1px 1px at 50px 160px, #fff, rgba(0,0,0,0)), radial-gradient(1px 1px at 90px 40px, #fff, rgba(0,0,0,0)), radial-gradient(1px 1px at 130px 80px, #fff, rgba(0,0,0,0))',
          backgroundSize: '200px 200px'
        }} />
      </div>

      {/* Starfield Layer 2: Medium twinkling stars */}
      <motion.div 
        animate={{ opacity: [0.3, 0.7, 0.3] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-0 opacity-30"
      >
        <div className="stars-medium absolute inset-0" style={{ 
          backgroundImage: 'radial-gradient(2px 2px at 100px 150px, #a855f7, rgba(0,0,0,0)), radial-gradient(2px 2px at 300px 400px, #d946ef, rgba(0,0,0,0)), radial-gradient(2px 2px at 500px 100px, #06b6d4, rgba(0,0,0,0))',
          backgroundSize: '600px 600px'
        }} />
      </motion.div>

      {/* Hyper-speed Light Trails - Reference 1 aesthetic */}
      <div className="absolute inset-0 opacity-[0.15]">
        <motion.div 
          animate={{ x: [-1000, 2000] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear", repeatDelay: 1 }}
          className="absolute top-[20%] left-0 w-[500px] h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent blur-sm"
        />
        <motion.div 
          animate={{ x: [-1500, 1500] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 2 }}
          className="absolute top-[60%] left-0 w-[800px] h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent blur-md"
        />
        <motion.div 
          animate={{ x: [2000, -1000] }}
          transition={{ duration: 5, repeat: Infinity, ease: "linear", repeatDelay: 0.5 }}
          className="absolute top-[40%] right-0 w-[600px] h-px bg-gradient-to-r from-transparent via-pink-500 to-transparent blur-sm"
        />
      </div>

      {/* Hexagonal Mesh Overlay */}
      <div className="absolute inset-0 opacity-[0.05]" style={{ 
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l25.98 15v30L30 60 4.02 45V15z' fill-opacity='0' stroke='%23a855f7' stroke-width='1'/%3E%3C/svg%3E")`,
        backgroundSize: '80px 80px'
      }} />

      {/* Specific Glowing Orbs per Brand Spec - Pulsing */}
      <div className="absolute top-[10%] left-[20%] w-[500px] h-[500px] bg-purple-600/10 blur-[150px] rounded-full animate-pulse-glow" />
      <div className="absolute bottom-[20%] right-[10%] w-[700px] h-[700px] bg-pink-600/10 blur-[180px] rounded-full animate-pulse-glow" style={{ animationDelay: '2s' }} />
      <div className="absolute top-[60%] left-[40%] w-[450px] h-[450px] bg-cyan-600/5 blur-[120px] rounded-full animate-pulse-glow" style={{ animationDelay: '4s' }} />

      {/* Grid Overlay per Spec: 60px spacing, subtle opacity */}
      <div 
        className="absolute inset-0 opacity-[0.03]" 
        style={{ 
          backgroundImage: `linear-gradient(#a855f7 1px, transparent 1px), linear-gradient(90deg, #a855f7 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} 
      />

      {/* Particle System */}
      <ParticleNetwork />

      {/* Circuit Board Lines (Animated Path) */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.05] pointer-events-none" viewBox="0 0 1000 1000">
        <g stroke="#a855f7" strokeWidth="1" fill="none">
          <path d="M100,200 L300,200 L350,250 L600,250 L650,200 L900,200" className="circuit-path" />
          <path d="M0,500 L200,500 L250,450 L400,450 L450,500 L1000,500" className="circuit-path" style={{ animationDelay: '2s' }} />
          <path d="M150,800 L350,800 L400,850 L700,850 L750,800 L950,800" className="circuit-path" style={{ animationDelay: '4s' }} />
        </g>
      </svg>
    </div>
  );
};

export default CosmicBackground;
